/**
 * SEO Component
 *
 * Manages document head meta tags for SEO optimization.
 * Supports multiple languages (FR/EN) with automatic detection.
 * Uses react-helmet-async for proper SSR support.
 *
 * Content inspired by Marketing_strategy.md
 */

import { Helmet } from 'react-helmet-async';

// Production base URL for canonical links (not the v6 dev subdomain)
export const PRODUCTION_BASE_URL = 'https://app.edouard.ai';

export type SupportedLanguage = 'fr' | 'en';

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  canonicalPath?: string;  // Path for canonical URL (e.g., '/portfolio', '/fluxnode-monitor')
  type?: 'website' | 'article' | 'product';
  locale?: string;
  siteName?: string;
  twitterCard?: 'summary' | 'summary_large_image';
  noIndex?: boolean;
}

export interface MultilingualSEO {
  fr: SEOProps;
  en: SEOProps;
}

// Detect user's preferred language
export function detectLanguage(): SupportedLanguage {
  if (typeof navigator === 'undefined') return 'en';

  const browserLang = navigator.language || (navigator as any).userLanguage || 'en';
  const langCode = browserLang.split('-')[0].toLowerCase();

  return langCode === 'fr' ? 'fr' : 'en';
}

// Get stored language preference or detect
export function getLanguage(): SupportedLanguage {
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem('app_language');
    if (stored === 'fr' || stored === 'en') return stored;
  }
  return detectLanguage();
}

const DEFAULT_SEO: SEOProps = {
  siteName: 'Edouard.ai',
  locale: 'en_US',
  type: 'website',
  twitterCard: 'summary_large_image',
  image: '/og/og-default.webp',
};

// Page-specific OG images mapping (WebP format for performance, widely supported)
const PAGE_OG_IMAGES: Record<string, string> = {
  'portfolio': '/og/og-portfolio.webp',
  'bitcoin-cycles': '/og/og-bitcoin-cycles.webp',
  'bot': '/og/og-bot.webp',
  'autopilot': '/og/og-autopilot.webp',
  'academy': '/og/og-academy.webp',
  'my-learning': '/og/og-academy.webp',
  'guide': '/og/og-guide.webp',
  'tax-strategies': '/og/og-tax-strategies.webp',
  'wallet-tracker': '/og/og-wallet-tracker.webp',
  'cryptocom-dashboard': '/og/og-cryptocom-dashboard.webp',
  'kucoin-dashboard': '/og/og-kucoin-dashboard.webp',
  'changelog': '/og/og-changelog.webp',
  'tax-loss-harvesting': '/og/og-tax-strategies.webp',
  'portfolio-rebalancing': '/og/og-portfolio.webp',
  'fluxnode-monitor': '/og/og-fluxnode-monitor.webp',
  'hedera-monitor': '/og/og-hedera-monitor.webp',
  'module-hub': '/og/og-module-hub.webp',
  'analytics-overview': '/og/og-advanced-analytics.webp',
  'analytics-performance': '/og/og-advanced-analytics.webp',
  'analytics-risk': '/og/og-advanced-analytics.webp',
  'analytics-trading': '/og/og-advanced-analytics.webp',
  'analytics-positions': '/og/og-advanced-analytics.webp',
  'analytics-insights': '/og/og-advanced-analytics.webp',
  'projects': '/og/og-projects.webp',
  'quiz': '/og/og-quiz.webp',
};

export function SEO({
  title,
  description,
  keywords = [],
  image,
  canonicalPath,
  type = 'website',
  locale,
  siteName = 'Edouard.ai',
  twitterCard = 'summary_large_image',
  noIndex = false,
}: SEOProps) {
  const fullTitle = title ? `${title} | ${siteName}` : siteName;
  // Always use production URL for canonical (not dev subdomain v6)
  const canonicalUrl = canonicalPath
    ? `${PRODUCTION_BASE_URL}${canonicalPath}`
    : PRODUCTION_BASE_URL;
  const imageUrl = image ? `${PRODUCTION_BASE_URL}${image}` : `${PRODUCTION_BASE_URL}${DEFAULT_SEO.image}`;
  const lang = getLanguage();
  const effectiveLocale = locale || (lang === 'fr' ? 'fr_FR' : 'en_US');

  return (
    <Helmet>
      {/* Language */}
      <html lang={lang} />

      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(', ')} />}

      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      {imageUrl && <meta property="og:image" content={imageUrl} />}
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={effectiveLocale} />

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      {imageUrl && <meta name="twitter:image" content={imageUrl} />}

      {/* Additional Meta */}
      <meta name="application-name" content={siteName} />
      <meta name="apple-mobile-web-app-title" content={siteName} />
      <meta name="theme-color" content="#6366f1" />

      {/* Canonical URL - Always points to production domain */}
      <link rel="canonical" href={canonicalUrl} />
    </Helmet>
  );
}

// Multilingual SEO configurations for each page
// Inspired by Marketing_strategy.md - USP & Feature descriptions
export const PAGE_SEO_CONFIG: Record<string, MultilingualSEO> = {
  'portfolio': {
    fr: {
      title: 'Portfolio Crypto - 100% Privé & Local',
      description: 'Module Portfolio de votre toolkit crypto. Gérez vos cryptos en toute confidentialité : calculs FIFO/LIFO, suivi des swaps, préservation du coût d\'origine. Données 100% locales, aucune inscription.',
      keywords: ['portfolio crypto privé', 'tracker crypto anonyme', 'portfolio crypto sans inscription', 'gestion crypto locale', 'P&L crypto', 'FIFO LIFO', 'localhost crypto'],
    },
    en: {
      title: 'Crypto Portfolio - 100% Private & Local',
      description: 'Portfolio module of your crypto toolkit. Manage your crypto with complete privacy: FIFO/LIFO calculations, swap tracking, cost basis preservation. 100% local data, no signup required.',
      keywords: ['private crypto portfolio', 'anonymous crypto tracker', 'no signup crypto portfolio', 'local crypto management', 'crypto P&L', 'FIFO LIFO', 'localhost crypto'],
    }
  },
  'bitcoin-cycles': {
    fr: {
      title: 'Cycles Bitcoin & Analyse EMA200',
      description: 'Maîtrisez les cycles du Bitcoin. Analyse EMA200, historique des halvings, projections bull/bear market et timing optimal pour vos investissements.',
      keywords: ['bitcoin cycles', 'halving bitcoin', 'EMA200 bitcoin', 'bull run prediction', 'bear market crypto', 'ath crypto vendre'],
    },
    en: {
      title: 'Bitcoin Cycles & EMA200 Analysis',
      description: 'Master Bitcoin cycles. EMA200 analysis, halving history, bull/bear market projections and optimal timing for your investments.',
      keywords: ['bitcoin cycles', 'bitcoin halving', 'EMA200 bitcoin', 'bull run prediction', 'bear market crypto', 'crypto ath sell'],
    }
  },
  'bot': {
    fr: {
      title: 'Bot Trading IA - Signaux Automatisés',
      description: 'Recevez des signaux de trading IA (Conservative/Balanced/Aggressive). Analyse de sentiment Twitter & Reddit, backtesting sur données historiques.',
      keywords: ['bot trading crypto', 'signaux trading crypto', 'bot crypto gratuit', 'trading automatique', 'sentiment analysis crypto'],
    },
    en: {
      title: 'AI Trading Bot - Automated Signals',
      description: 'Get AI trading signals (Conservative/Balanced/Aggressive). Twitter & Reddit sentiment analysis, backtesting on historical data.',
      keywords: ['crypto trading bot', 'crypto trading signals', 'free crypto bot', 'automated trading', 'crypto sentiment analysis'],
    }
  },
  'autopilot': {
    fr: {
      title: 'Autopilot - Bots DCA, Grid & Rebalancer',
      description: 'Bots de trading automatisés : Infinity Grid, Smart DCA, BTC Rebalancer. Connexion CEX (Binance, KuCoin) ou DEX (Hyperliquid, Aster). Trading 24/7.',
      keywords: ['autopilot crypto', 'bot dca crypto gratuit', 'grid trading crypto', 'binance bot', 'trading automatisé 24/7'],
    },
    en: {
      title: 'Autopilot - DCA, Grid & Rebalancer Bots',
      description: 'Automated trading bots: Infinity Grid, Smart DCA, BTC Rebalancer. CEX connection (Binance, KuCoin) or DEX (Hyperliquid, Aster). 24/7 trading.',
      keywords: ['crypto autopilot', 'free dca crypto bot', 'grid trading crypto', 'binance bot', 'automated trading 24/7'],
    }
  },
  'academy': {
    fr: {
      title: 'Academy - 100+ Cours Crypto & Trading',
      description: 'Devenez un expert crypto. 100+ cours structurés Netflix-style, parcours progressifs, quiz interactifs, système d\'achievements et certifications.',
      keywords: ['formation crypto', 'cours trading crypto', 'apprendre crypto', 'academy blockchain', 'certification crypto'],
    },
    en: {
      title: 'Academy - 100+ Crypto & Trading Courses',
      description: 'Become a crypto expert. 100+ Netflix-style structured courses, progressive paths, interactive quizzes, achievement system and certifications.',
      keywords: ['crypto education', 'crypto trading courses', 'learn crypto', 'blockchain academy', 'crypto certification'],
    }
  },
  'my-learning': {
    fr: {
      title: 'Mon Apprentissage - Progression & Achievements',
      description: 'Suivez votre progression d\'apprentissage. Achievements débloqués, badges gagnés, objectifs personnels et statistiques de votre parcours crypto.',
      keywords: ['progression crypto', 'achievements crypto', 'apprentissage trading', 'formation suivi'],
    },
    en: {
      title: 'My Learning - Progress & Achievements',
      description: 'Track your learning progress. Unlocked achievements, earned badges, personal goals and statistics of your crypto journey.',
      keywords: ['crypto progress', 'crypto achievements', 'trading learning', 'education tracking'],
    }
  },
  'guide': {
    fr: {
      title: 'Guide Complet Edouard.ai',
      description: 'Documentation complète d\'Edouard.ai. Toutes les fonctionnalités expliquées, tutoriels pas-à-pas, FAQ et roadmap du projet.',
      keywords: ['guide edouard.ai', 'documentation crypto', 'tutoriel portfolio', 'faq crypto app'],
    },
    en: {
      title: 'Complete Edouard.ai Guide',
      description: 'Complete Edouard.ai documentation. All features explained, step-by-step tutorials, FAQ and project roadmap.',
      keywords: ['edouard.ai guide', 'crypto documentation', 'portfolio tutorial', 'crypto app faq'],
    }
  },
  'tax-strategies': {
    fr: {
      title: 'Stratégies Fiscales Crypto France',
      description: 'Optimisez légalement votre fiscalité crypto. Crédit Lombard, flat tax 30%, stratégies DeFi (AAVE), formulaire 2086 et conformité MiCA.',
      keywords: ['fiscalité crypto france', 'impot crypto', 'flat tax crypto', 'formulaire 2086 crypto', 'déclaration crypto impots'],
    },
    en: {
      title: 'Crypto Tax Strategies',
      description: 'Legally optimize your crypto taxes. Lombard credit, flat tax strategies, DeFi optimization (AAVE), tax form assistance and MiCA compliance.',
      keywords: ['crypto taxes', 'bitcoin taxes', 'crypto tax optimization', 'tax loss harvesting crypto', 'crypto tax report'],
    }
  },
  'wallet-tracker': {
    fr: {
      title: 'Wallet Tracker - Whale & Smart Money',
      description: 'Suivez les wallets des baleines et du smart money. Surveillance multi-chain EVM, alertes transactions, analyse on-chain avec Arkham Intelligence.',
      keywords: ['wallet tracker', 'whale watching crypto', 'smart money tracker', 'ethereum wallet tracker', 'arkham crypto intelligence'],
    },
    en: {
      title: 'Wallet Tracker - Whale & Smart Money',
      description: 'Track whale and smart money wallets. Multi-chain EVM surveillance, transaction alerts, on-chain analysis with Arkham Intelligence.',
      keywords: ['wallet tracker', 'crypto whale watching', 'smart money tracker', 'ethereum wallet tracker', 'arkham crypto intelligence'],
    }
  },
  'cryptocom-dashboard': {
    fr: {
      title: 'Dashboard Crypto.com - CRO & Carte Visa',
      description: 'Dashboard dédié Crypto.com. Suivi CRO, gestion carte Visa crypto, staking, bots de trading et analyse des récompenses cashback.',
      keywords: ['crypto.com', 'CRO token', 'carte visa crypto', 'crypto.com staking', 'cashback crypto'],
    },
    en: {
      title: 'Crypto.com Dashboard - CRO & Visa Card',
      description: 'Dedicated Crypto.com dashboard. CRO tracking, crypto Visa card management, staking, trading bots and cashback rewards analysis.',
      keywords: ['crypto.com', 'CRO token', 'crypto visa card', 'crypto.com staking', 'crypto cashback'],
    }
  },
  'kucoin-dashboard': {
    fr: {
      title: 'Dashboard KuCoin - Bots & Earn',
      description: 'Dashboard dédié KuCoin. Trading bots, KuCoin Earn, programme VIP, import automatique des transactions et analyse de performance.',
      keywords: ['kucoin', 'kucoin trading bot', 'kucoin earn', 'kucoin vip', 'portfolio kucoin'],
    },
    en: {
      title: 'KuCoin Dashboard - Bots & Earn',
      description: 'Dedicated KuCoin dashboard. Trading bots, KuCoin Earn, VIP program, automatic transaction import and performance analysis.',
      keywords: ['kucoin', 'kucoin trading bot', 'kucoin earn', 'kucoin vip', 'kucoin portfolio'],
    }
  },
  'changelog': {
    fr: {
      title: 'Changelog & Mises à jour',
      description: 'Découvrez les dernières nouveautés d\'Edouard.ai. Historique des versions, nouvelles fonctionnalités et améliorations de la plateforme.',
      keywords: ['changelog', 'mises à jour', 'nouvelles fonctionnalités', 'edouard.ai updates'],
    },
    en: {
      title: 'Changelog & Updates',
      description: 'Discover the latest Edouard.ai news. Version history, new features and platform improvements.',
      keywords: ['changelog', 'updates', 'new features', 'edouard.ai updates'],
    }
  },
  'logs': {
    fr: {
      title: 'Logs Système',
      description: 'Consultez les logs système pour le debugging et le monitoring de l\'application.',
      keywords: ['logs', 'système', 'debug'],
      noIndex: true,
    },
    en: {
      title: 'System Logs',
      description: 'View system logs for debugging and application monitoring.',
      keywords: ['logs', 'system', 'debug'],
      noIndex: true,
    }
  },
  'tax-loss-harvesting': {
    fr: {
      title: 'Tax Loss Harvesting Crypto',
      description: 'Identifiez les opportunités de tax loss harvesting. Réalisez stratégiquement vos moins-values pour optimiser votre fiscalité crypto.',
      keywords: ['tax loss harvesting crypto', 'moins-values crypto', 'optimisation fiscale', 'impôts crypto réduire'],
    },
    en: {
      title: 'Crypto Tax Loss Harvesting',
      description: 'Identify tax loss harvesting opportunities. Strategically realize your losses to optimize your crypto taxes.',
      keywords: ['tax loss harvesting crypto', 'crypto capital losses', 'tax optimization', 'reduce crypto taxes'],
    }
  },
  'portfolio-rebalancing': {
    fr: {
      title: 'Rééquilibrage Portfolio Crypto',
      description: 'Rééquilibrez intelligemment votre portfolio crypto. Suggestions basées sur vos objectifs d\'allocation, optimisation de la diversification.',
      keywords: ['rebalancing crypto', 'rééquilibrage portfolio', 'allocation crypto', 'diversification portfolio'],
    },
    en: {
      title: 'Crypto Portfolio Rebalancing',
      description: 'Intelligently rebalance your crypto portfolio. Suggestions based on your allocation goals, diversification optimization.',
      keywords: ['crypto rebalancing', 'portfolio rebalancing', 'crypto allocation', 'portfolio diversification'],
    }
  },
  'analytics-insights': {
    fr: {
      title: 'Insights - Décisions & Métriques',
      description: 'Métriques décisionnelles : Sharpe Ratio, Max Drawdown, score de santé du portfolio, suggestions personnalisées de rebalance.',
      keywords: ['insights crypto', 'sharpe ratio crypto', 'max drawdown', 'score santé portfolio', 'suggestions rebalance'],
    },
    en: {
      title: 'Insights - Decisions & Metrics',
      description: 'Decision-making metrics: Sharpe Ratio, Max Drawdown, portfolio health score, personalized rebalancing suggestions.',
      keywords: ['crypto insights', 'crypto sharpe ratio', 'max drawdown', 'portfolio health', 'rebalancing suggestions'],
    }
  },
  'seed-vault': {
    fr: {
      title: 'Seed Vault Sécurisé',
      description: 'Sécurisez vos seed phrases avec chiffrement AES-256-GCM. Stéganographie pour cacher vos phrases dans des images. Export QR code.',
      keywords: ['seed vault', 'sécuriser seed phrase', 'chiffrement crypto', 'stéganographie seed'],
      noIndex: true,
    },
    en: {
      title: 'Secure Seed Vault',
      description: 'Secure your seed phrases with AES-256-GCM encryption. Steganography to hide your phrases in images. QR code export.',
      keywords: ['seed vault', 'secure seed phrase', 'crypto encryption', 'seed steganography'],
      noIndex: true,
    }
  },
  'fluxnode-monitor': {
    fr: {
      title: 'FluxNode Monitor - Cumulus, Nimbus, Stratus',
      description: 'Surveillez vos FluxNodes en temps réel. Récompenses FLUX, uptime, APY par tier, ROI calculator et tracking des gains/pertes du collateral.',
      keywords: ['fluxnode', 'flux node monitor', 'flux node roi calculator', 'cumulus nimbus stratus', 'flux rewards', 'surveillance node crypto'],
    },
    en: {
      title: 'FluxNode Monitor - Cumulus, Nimbus, Stratus',
      description: 'Monitor your FluxNodes in real-time. FLUX rewards, uptime, APY by tier, ROI calculator and collateral gain/loss tracking.',
      keywords: ['fluxnode', 'flux node monitor', 'flux node roi calculator', 'cumulus nimbus stratus', 'flux rewards', 'crypto node monitoring'],
    }
  },
  'hedera-monitor': {
    fr: {
      title: 'Hedera Monitor - HBAR & Staking',
      description: 'Suivez vos comptes Hedera HBAR. Staking rewards, tokens HTS, NFTs et analyse complète de votre portfolio Hashgraph.',
      keywords: ['hedera', 'HBAR', 'hedera hbar staking', 'hedera account tracker', 'hashgraph portfolio'],
    },
    en: {
      title: 'Hedera Monitor - HBAR & Staking',
      description: 'Track your Hedera HBAR accounts. Staking rewards, HTS tokens, NFTs and complete Hashgraph portfolio analysis.',
      keywords: ['hedera', 'HBAR', 'hedera hbar staking', 'hedera account tracker', 'hashgraph portfolio'],
    }
  },
  'module-hub': {
    fr: {
      title: 'Hub de Modules - Votre Couteau Suisse Crypto',
      description: 'Configurez votre boîte à outils crypto. Activez/désactivez les modules selon vos besoins : portfolio, bots trading, analytics, monitoring nodes. 100% privé, vos données restent locales.',
      keywords: ['modules crypto', 'toolkit crypto modulaire', 'personnalisation dashboard', 'app crypto privacy', 'localhost crypto'],
    },
    en: {
      title: 'Module Hub - Your Crypto Swiss-Army-Knife',
      description: 'Configure your crypto toolkit. Enable/disable modules as needed: portfolio, trading bots, analytics, node monitoring. 100% private, your data stays local.',
      keywords: ['crypto modules', 'modular crypto toolkit', 'dashboard customization', 'privacy crypto app', 'localhost crypto'],
    }
  },
  'analytics-overview': {
    fr: {
      title: 'Vue d\'Ensemble Portfolio',
      description: 'Vue d\'ensemble complète de votre portfolio crypto. KPIs essentiels, allocation par catégorie, top performers et métriques clés.',
      keywords: ['portfolio overview', 'KPI crypto', 'allocation portfolio', 'métriques portfolio'],
    },
    en: {
      title: 'Portfolio Overview',
      description: 'Complete overview of your crypto portfolio. Essential KPIs, category allocation, top performers and key metrics.',
      keywords: ['portfolio overview', 'crypto KPI', 'portfolio allocation', 'portfolio metrics'],
    }
  },
  'analytics-performance': {
    fr: {
      title: 'Analyse de Performance',
      description: 'Analysez vos performances en profondeur. Évolution temporelle, drawdown maximum, rendements périodiques et comparaison aux indices.',
      keywords: ['performance crypto', 'rendement portfolio', 'drawdown analyse', 'ROI crypto'],
    },
    en: {
      title: 'Performance Analysis',
      description: 'Analyze your performance in depth. Time evolution, maximum drawdown, periodic returns and index comparison.',
      keywords: ['crypto performance', 'portfolio returns', 'drawdown analysis', 'crypto ROI'],
    }
  },
  'analytics-risk': {
    fr: {
      title: 'Analyse de Risque',
      description: 'Évaluez le risque de votre portfolio. Matrice de corrélation, volatilité, VaR, score de diversification et alertes de concentration.',
      keywords: ['risque crypto', 'corrélation crypto', 'VaR portfolio', 'diversification score'],
    },
    en: {
      title: 'Risk Analysis',
      description: 'Evaluate your portfolio risk. Correlation matrix, volatility, VaR, diversification score and concentration alerts.',
      keywords: ['crypto risk', 'crypto correlation', 'portfolio VaR', 'diversification score'],
    }
  },
  'analytics-trading': {
    fr: {
      title: 'Outils de Trading',
      description: 'Outils de trading pour traders actifs. Analyse technique, volumes, liquidité, sentiment de marché et signaux en temps réel.',
      keywords: ['trading tools crypto', 'analyse technique', 'volume trading', 'sentiment marché crypto'],
    },
    en: {
      title: 'Trading Tools',
      description: 'Trading tools for active traders. Technical analysis, volumes, liquidity, market sentiment and real-time signals.',
      keywords: ['crypto trading tools', 'technical analysis', 'trading volume', 'crypto market sentiment'],
    }
  },
  'analytics-positions': {
    fr: {
      title: 'Gestion des Positions',
      description: 'Gérez vos positions comme un pro. Targets, stop-loss, durée de détention, suggestions de rééquilibrage et suivi P&L par position.',
      keywords: ['gestion positions crypto', 'stop loss crypto', 'target price', 'P&L position'],
    },
    en: {
      title: 'Position Management',
      description: 'Manage your positions like a pro. Targets, stop-loss, holding duration, rebalancing suggestions and P&L tracking per position.',
      keywords: ['crypto position management', 'crypto stop loss', 'target price', 'position P&L'],
    }
  },
  'settings': {
    fr: {
      title: 'Paramètres',
      description: 'Configurez Edouard.ai. Thème dark/light, sécurité (PIN, biométrie), notifications et préférences personnelles.',
      keywords: ['paramètres', 'settings', 'configuration'],
      noIndex: true,
    },
    en: {
      title: 'Settings',
      description: 'Configure Edouard.ai. Dark/light theme, security (PIN, biometric), notifications and personal preferences.',
      keywords: ['settings', 'configuration'],
      noIndex: true,
    }
  },
  'projects': {
    fr: {
      title: 'Projets Communautaires - SecureFlux & Edouard.ai V2',
      description: 'Découvrez nos projets en développement : SecureFlux Messenger (messagerie chiffrée post-quantique Kyber-1024) et Edouard.ai V2 (cloud sync & mobile). Votez, soutenez et suivez la progression.',
      keywords: ['projets crypto', 'secureflux messenger', 'messagerie chiffrée', 'kyber-1024', 'edouard.ai v2', 'projets communautaires', 'open source crypto', 'flux ecosystem'],
    },
    en: {
      title: 'Community Projects - SecureFlux & Edouard.ai V2',
      description: 'Discover our projects in development: SecureFlux Messenger (post-quantum encrypted messaging with Kyber-1024) and Edouard.ai V2 (cloud sync & mobile). Vote, support and track progress.',
      keywords: ['crypto projects', 'secureflux messenger', 'encrypted messaging', 'kyber-1024', 'edouard.ai v2', 'community projects', 'open source crypto', 'flux ecosystem'],
    }
  },
  'quiz': {
    fr: {
      title: 'Quiz Crypto - 82 Questions pour Tester vos Connaissances',
      description: 'Testez vos connaissances crypto avec 82 questions sur Bitcoin, DeFi, Staking, Sécurité et plus. Gagnez de l\'XP, débloquez des achievements et apprenez avec des explications détaillées.',
      keywords: ['quiz crypto', 'test bitcoin', 'quiz blockchain', 'questions crypto', 'apprendre crypto', 'quiz defi', 'quiz staking', 'education crypto'],
    },
    en: {
      title: 'Crypto Quiz - 82 Questions to Test Your Knowledge',
      description: 'Test your crypto knowledge with 82 questions on Bitcoin, DeFi, Staking, Security and more. Earn XP, unlock achievements and learn with detailed explanations.',
      keywords: ['crypto quiz', 'bitcoin test', 'blockchain quiz', 'crypto questions', 'learn crypto', 'defi quiz', 'staking quiz', 'crypto education'],
    }
  },
};

// Default SEO for unknown pages
const DEFAULT_PAGE_SEO: MultilingualSEO = {
  fr: {
    title: 'Edouard.ai - Boîte à Outils Crypto Modulaire',
    description: 'Boîte à outils modulaire type couteau suisse pour investisseurs crypto. 100% privé, localhost-first & API. Activez/désactivez les modules selon vos besoins : portfolio, bots, analytics, monitoring.',
    keywords: ['toolkit crypto', 'app crypto privée', 'crypto localhost', 'plateforme modulaire crypto', 'trading bot', 'privacy crypto', 'sans inscription'],
  },
  en: {
    title: 'Edouard.ai - Swiss-Army-Knife Crypto Toolkit',
    description: 'Swiss-army-knife modular toolkit for crypto investors. 100% privacy, localhost-first & API-powered. Enable/disable modules as you wish: portfolio, bots, analytics, node monitoring & more.',
    keywords: ['crypto toolkit', 'privacy crypto app', 'localhost crypto', 'modular crypto platform', 'trading bot', 'no signup crypto', 'API crypto tools'],
  }
};

// Helper to get SEO config for a page in the current language
// Automatically adds canonicalPath based on pageId
export function getPageSEO(pageId: string): SEOProps {
  const lang = getLanguage();
  const config = PAGE_SEO_CONFIG[pageId] || DEFAULT_PAGE_SEO;
  const seoProps = config[lang];
  // Generate canonical path from page ID (e.g., 'portfolio' -> '/portfolio')
  const canonicalPath = pageId && pageId !== 'home' ? `/${pageId}` : '/';
  // Get page-specific OG image or use default
  const image = PAGE_OG_IMAGES[pageId] || DEFAULT_SEO.image;
  return { ...seoProps, canonicalPath, image };
}

// Helper to get SEO config for a specific language
export function getPageSEOForLang(pageId: string, lang: SupportedLanguage): SEOProps {
  const config = PAGE_SEO_CONFIG[pageId] || DEFAULT_PAGE_SEO;
  const seoProps = config[lang];
  const canonicalPath = pageId && pageId !== 'home' ? `/${pageId}` : '/';
  // Get page-specific OG image or use default
  const image = PAGE_OG_IMAGES[pageId] || DEFAULT_SEO.image;
  return { ...seoProps, canonicalPath, image };
}

// Set language preference
export function setLanguage(lang: SupportedLanguage): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('app_language', lang);
  }
}
